import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/lib/api-client';

export type MasterkasaSource = {
  id: string;
  name: string;
  host: string;
  enabled: boolean;
  fullImportRequired: boolean;
  importStatus: 'idle' | 'running' | 'ok' | 'failed';
  lastProcessedSynchroId: string;
  activeGeneration: string | null;
  lastSuccessfulImportAt: string | null;
  lastRecordErrorCount: number;
  /** Non-fatal omissions from the currently published terminal catalogue. */
  catalogueWarning?: string | null;
  /**
   * VL-73: store logins another active cashier of the store also answers to;
   * the tills refuse those sign-ins. Fixed-format, non-secret.
   */
  cashierWarning?: string | null;
  /** This store's `shop_id` in a shared Masterkasa database (VL-291). */
  shopId: string | null;
  /** ISO-4217 currency of `t_bars.price` in this store's database. */
  currencyCode: string;
  /** EAV key overriding the article's own age flag; normally null (VL-290). */
  ageAssetKey: string | null;
  /** The age a product flagged `islimit = 1` carries (VL-290). */
  restrictedAgeYears: number;
  /** Source tax groups restricted whatever the flag says — lottery (VL-290). */
  ageRestrictedTaxGroups: number[];
  location: { id: string; code: string; name: string };
  /** Export into the store system (VL-291); settings authored in the source dialog (RL-453). */
  exportEnabled?: boolean;
  exportZReports?: boolean;
  exportFrom?: string | null;
  exportUsername?: string | null;
  /** Whether a write password is stored. The password itself is never returned. */
  exportPasswordSet?: boolean;
  cashInReceiptType?: number;
  cashOutReceiptType?: number;
  lastExportAt?: string | null;
  lastExportErrorText?: string | null;
  /**
   * RL-467: how many of the store's promotion objects became promotions and
   * how many did not (each with its reasons under `sources/:id/promotions`).
   * Absent on an older Back Office.
   */
  promotionObjects?: { mapped: number; unmapped: number };
};

/** RL-467: one promotion object of the store system and what became of it. */
export type MasterkasaPromotionObject = {
  uid: number;
  /** Null for properties whose object no longer exists. */
  name: string | null;
  note: string | null;
  family: string;
  status: 'mapped' | 'unmapped';
  reasons: string[];
  promotionId: string | null;
  updatedAt: string;
};

export type MasterkasaPromotionObjects = {
  counts: { mapped: number; unmapped: number };
  items: MasterkasaPromotionObject[];
};

/** What an operator fills in to add a store (VL-290). */
export type MasterkasaSourceInput = {
  locationId: string;
  name: string;
  host: string;
  port: number;
  databaseName: string;
  username: string;
  password: string;
  ssl: boolean;
  shopId: string | null;
  currencyCode: string;
  restrictedAgeYears: number;
  ageRestrictedTaxGroups: number[];
  ageAssetKey: string | null;
};

/** The subset a source's settings dialog may change. */
export type MasterkasaSourceSettings = {
  shopId: string | null;
  currencyCode: string;
  restrictedAgeYears: number;
  ageRestrictedTaxGroups: number[];
  ageAssetKey: string | null;
  /** Export fields travel only when the operator changed them (RL-453). */
  exportEnabled?: boolean;
  exportZReports?: boolean;
  exportUsername?: string | null;
  /** Write-only: sent when typed, never read back. */
  exportPassword?: string;
  cashInReceiptType?: number;
  cashOutReceiptType?: number;
};

export type MasterkasaExportStatus = 'pending' | 'written' | 'failed' | 'conflict';

/** One document on its way to the store system (`GET /connectors/sources/:id/exports`). */
export type MasterkasaExportRow = {
  id: string;
  kind: string;
  documentId: string;
  status: MasterkasaExportStatus;
  kassaNumber: number | null;
  receiptNumber: number | null;
  attempts: number;
  lastError: string | null;
  documentAt: string;
  nextAttemptAt: string;
  writtenAt: string | null;
};

export type MasterkasaExports = {
  counts: Partial<Record<MasterkasaExportStatus, number>>;
  items: MasterkasaExportRow[];
};

const KEY = ['masterkasa-sources'] as const;

export function useMasterkasaSources(enabled: boolean) {
  return useQuery({
    queryKey: KEY,
    enabled,
    refetchInterval: 5_000,
    queryFn: async (): Promise<MasterkasaSource[]> => {
      const response = await apiClient.get<MasterkasaSource[]>('/connectors/sources');
      return response.data;
    },
  });
}

export function useCreateMasterkasaSource() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: MasterkasaSourceInput) => {
      await apiClient.post('/connectors/sources', input);
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: KEY }),
  });
}

export function useUpdateMasterkasaSource() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: { sourceId: string; settings: MasterkasaSourceSettings }) => {
      await apiClient.patch(`/connectors/sources/${input.sourceId}`, input.settings);
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: KEY }),
  });
}

export function useRunMasterkasaImport() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      sourceId: string;
      mode: 'full' | 'incremental';
      startSynchroId?: number;
    }) => {
      await apiClient.post(`/connectors/sources/${input.sourceId}/imports`, {
        mode: input.mode,
        startSynchroId: input.startSynchroId,
      });
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: KEY }),
  });
}

const exportsKey = (sourceId: string) => ['masterkasa-exports', sourceId] as const;

/** RL-453: what a Back Office-hosted export has not sent, and why. */
export function useMasterkasaExports(sourceId: string, enabled: boolean) {
  return useQuery({
    queryKey: exportsKey(sourceId),
    enabled,
    refetchInterval: 10_000,
    queryFn: async (): Promise<MasterkasaExports> => {
      const response = await apiClient.get<MasterkasaExports>(
        `/connectors/sources/${sourceId}/exports`,
        { params: { limit: 200 } },
      );
      return response.data;
    },
  });
}

/** RL-467: every promotion object of [sourceId]'s store; null = not loaded. */
export function useMasterkasaPromotionObjects(sourceId: string | null) {
  return useQuery({
    queryKey: ['masterkasa-promotion-objects', sourceId] as const,
    enabled: sourceId !== null,
    queryFn: async () => {
      const response = await apiClient.get<MasterkasaPromotionObjects>(
        `/connectors/sources/${sourceId}/promotions`,
      );
      return response.data;
    },
  });
}

export function useRetryMasterkasaExport() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: { sourceId: string; exportId: string }) => {
      await apiClient.post(`/connectors/sources/${input.sourceId}/exports/${input.exportId}/retry`);
    },
    onSuccess: (_data, input) =>
      queryClient.invalidateQueries({ queryKey: exportsKey(input.sourceId) }),
  });
}
